import { Injectable, inject, signal } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Router } from '@angular/router';
import { Observable } from 'rxjs';
import { map, catchError, tap } from 'rxjs/operators';
import { of } from 'rxjs';
import { ENV } from '../environment';
import { FeatureCapabilitiesService } from './feature-capabilities.service';

export interface User {
  email: string;
  name: string;
}

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  private http = inject(HttpClient);
  private router = inject(Router);
  private featureCapabilitiesService = inject(FeatureCapabilitiesService);
  private apiUrl = `${ENV.baseUrl}auth`;

  // Using signals for reactive state management
  currentUser = signal<User | null>(null);
  isLoading = signal<boolean>(false);

  constructor() {
    this.checkCurrentUser();
  }

  // Ensures we have attempted to fetch the current user before guards decide.
  ensureAuthenticated(): Observable<boolean> {
    if (this.isAuthenticated()) {
      return of(true);
    }
    return this.http.get<any>(`${this.apiUrl}/user`, {
      withCredentials: true
    }).pipe(
      map(response => {
        if (response) {
          this.currentUser.set(response);
          // Load capabilities after successful authentication
          this.featureCapabilitiesService.getCapabilities().subscribe();
          return true;
        }
        return false;
      }),
      catchError(() => of(false))
    );
  }

  login(email: string, password: string): Observable<{ success: boolean; error?: string }> {
    this.isLoading.set(true);

    return this.http.post<any>(`${this.apiUrl}/login`, { email, password }, {
      withCredentials: true // Include cookies for session management
    }).pipe(
      map(response => {
        this.currentUser.set(response.user);
        this.isLoading.set(false);
        // Load capabilities after successful login
        this.featureCapabilitiesService.getCapabilities().subscribe();
        return { success: true };
      }),
      catchError(error => {
        this.isLoading.set(false);
        return of({
          success: false,
          error: error.error?.error || 'Login failed'
        });
      })
    );
  }

  signup(email: string, password: string, name?: string): Observable<{ success: boolean; error?: string }> {
    this.isLoading.set(true);

    return this.http.post<any>(`${this.apiUrl}/signup`, { email, password, name }, {
      withCredentials: true
    }).pipe(
      map(response => {
        this.currentUser.set(response.user);
        this.isLoading.set(false);
        // Load capabilities after successful signup
        this.featureCapabilitiesService.getCapabilities().subscribe();
        return { success: true };
      }),
      catchError(error => {
        this.isLoading.set(false);
        return of({
          success: false,
          error: error.error?.error || 'Signup failed'
        });
      })
    );
  }

  logout(): Observable<void> {
    return this.http.post<any>(`${this.apiUrl}/logout`, {}, {
      withCredentials: true
    }).pipe(
      map(() => {
        this.currentUser.set(null);
        // Clear capabilities on logout
        this.featureCapabilitiesService.clearCapabilities();
        this.router.navigate(['/auth/login']);
      }),
      catchError(() => {
        // Even if logout fails on server, clear local state
        this.currentUser.set(null);
        this.featureCapabilitiesService.clearCapabilities();
        this.router.navigate(['/auth/login']);
        return of(void 0);
      })
    );
  }

  private checkCurrentUser(): void {
    this.http.get<any>(`${this.apiUrl}/user`, {
      withCredentials: true
    }).pipe(
      catchError(() => of(null))
    ).subscribe(response => {
      if (response) {
        this.currentUser.set(response);
        // Load capabilities after successful user check
        this.featureCapabilitiesService.getCapabilities().subscribe();
      }
    });
  }

  sendMagicLink(email: string): Observable<{ success: boolean; error?: string }> {
    this.isLoading.set(true);
    
    return this.http.post<any>(`${this.apiUrl}/magic-link`, { email }, {
      withCredentials: true
    }).pipe(
      map(response => {
        this.isLoading.set(false);
        return { success: true };
      }),
      catchError(error => {
        this.isLoading.set(false);
        return of({ 
          success: false, 
          error: error.error?.error || 'Failed to send magic link' 
        });
      })
    );
  }

  resetPassword(email: string): Observable<{ success: boolean; error?: string }> {
    this.isLoading.set(true);
    
    return this.http.post<any>(`${this.apiUrl}/reset-password`, { email }, {
      withCredentials: true
    }).pipe(
      map(response => {
        this.isLoading.set(false);
        return { success: true, message: response.message };
      }),
      catchError(error => {
        this.isLoading.set(false);
        return of({ 
          success: false, 
          error: error.error?.error || 'Failed to initiate password reset' 
        });
      })
    );
  }

  verifyMagicLink(token: string): Observable<{ success: boolean; error?: string }> {
    this.isLoading.set(true);

    return this.http.post<any>(`${this.apiUrl}/verify-magic-link`, { token }, {
      withCredentials: true
    }).pipe(
      map(response => {
        this.currentUser.set(response.user);
        this.isLoading.set(false);
        // Load capabilities after successful magic link verification
        this.featureCapabilitiesService.getCapabilities().subscribe();
        return { success: true };
      }),
      catchError(error => {
        this.isLoading.set(false);
        return of({
          success: false,
          error: error.error?.error || 'Failed to verify magic link'
        });
      })
    );
  }

  resetPasswordWithToken(token: string, newPassword: string): Observable<{ success: boolean; error?: string }> {
    return of({
      success: false,
      error: 'Password reset confirmation not yet implemented'
    });
  }

  updateProfile(name: string, email: string): Observable<{ success: boolean; error?: string; user?: User }> {
    this.isLoading.set(true);

    return this.http.post<any>(`${this.apiUrl}/update-profile`, { name, email }, {
      withCredentials: true
    }).pipe(
      map(response => {
        this.currentUser.set(response.user);
        this.isLoading.set(false);
        return { success: true, user: response.user };
      }),
      catchError(error => {
        this.isLoading.set(false);
        return of({
          success: false,
          error: error.error?.error || 'Failed to update profile'
        });
      })
    );
  }

  changePassword(currentPassword: string, newPassword: string): Observable<{ success: boolean; error?: string }> {
    this.isLoading.set(true);

    return this.http.post<any>(`${this.apiUrl}/change-password`, {
      currentPassword,
      newPassword
    }, {
      withCredentials: true
    }).pipe(
      map(response => {
        this.isLoading.set(false);
        return { success: true };
      }),
      catchError(error => {
        this.isLoading.set(false);
        return of({
          success: false,
          error: error.error?.error || 'Failed to change password'
        });
      })
    );
  }

  setPassword(newPassword: string): Observable<{ success: boolean; error?: string }> {
    this.isLoading.set(true);

    return this.http.post<any>(`${this.apiUrl}/set-password`, {
      newPassword
    }, {
      withCredentials: true
    }).pipe(
      map(response => {
        this.isLoading.set(false);
        return { success: true };
      }),
      catchError(error => {
        this.isLoading.set(false);
        return of({
          success: false,
          error: error.error?.error || 'Failed to set password'
        });
      })
    );
  }

  hasPassword(): Observable<boolean> {
    return this.http.get<any>(`${this.apiUrl}/has-password`, {
      withCredentials: true
    }).pipe(
      map(response => response.hasPassword),
      catchError(() => of(true)) // Default to true if error
    );
  }

  isAuthenticated(): boolean {
    return this.currentUser() !== null;
  }

  loginWithGoogle(idToken: string): Observable<{ success: boolean; error?: string }> {
    this.isLoading.set(true);

    return this.http.post<any>(`${this.apiUrl}/google`, { idToken }, {
      withCredentials: true
    }).pipe(
      map(response => {
        this.currentUser.set(response.user);
        this.isLoading.set(false);
        // Load capabilities after successful Google login
        this.featureCapabilitiesService.getCapabilities().subscribe();
        return { success: true };
      }),
      catchError(error => {
        this.isLoading.set(false);
        return of({
          success: false,
          error: error.error?.error || 'Google authentication failed'
        });
      })
    );
  }
}
